import React from "react";
import { FileSignature, Clock, PenLine, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";

const STAT_CARDS = [
  {
    key: "total",
    label: "Tổng yêu cầu",
    shortLabel: "Tổng",
    hint: "Toàn bộ phiếu",
    icon: FileSignature,
    tone: {
      shell: "from-slate-800 via-slate-700 to-slate-900 shadow-slate-900/30",
      shellActive:
        "from-slate-900 via-slate-800 to-black shadow-slate-950/40 ring-2 ring-white/25",
      motif: "text-white/15",
      soft: "text-slate-300",
      accent: "bg-white/10",
    },
  },
  {
    key: "pending",
    label: "Chờ xử lý",
    shortLabel: "Chờ",
    hint: "Cần duyệt / ký",
    icon: Clock,
    tone: {
      shell: "from-amber-700 via-amber-600 to-orange-800 shadow-amber-900/35",
      shellActive:
        "from-amber-800 via-amber-700 to-orange-900 shadow-amber-950/45 ring-2 ring-amber-200/40",
      motif: "text-white/18",
      soft: "text-amber-50/90",
      accent: "bg-white/10",
    },
  },
  {
    key: "signed",
    label: "Đã ký",
    shortLabel: "Đã ký",
    hint: "Đã có chữ ký",
    icon: PenLine,
    tone: {
      shell: "from-red-700 via-red-600 to-rose-800 shadow-red-900/35",
      shellActive:
        "from-red-800 via-red-700 to-rose-900 shadow-red-950/45 ring-2 ring-red-200/35",
      motif: "text-white/18",
      soft: "text-red-100/90",
      accent: "bg-white/10",
    },
  },
  {
    key: "completed",
    label: "Hoàn tất",
    shortLabel: "Xong",
    hint: "Đã hoàn thành",
    icon: CheckCircle2,
    tone: {
      shell: "from-emerald-700 via-emerald-600 to-teal-800 shadow-emerald-900/35",
      shellActive:
        "from-emerald-800 via-emerald-700 to-teal-900 shadow-emerald-950/45 ring-2 ring-emerald-200/35",
      motif: "text-white/18",
      soft: "text-emerald-50/90",
      accent: "bg-white/10",
    },
  },
];

const SigningMiniDashboard = ({ stats = {}, activeKey = "total", onSelect }) => (
  <div className="mb-4 md:mb-6" data-tour="hrm-signing-dashboard">
    <div className="grid grid-cols-4 gap-1.5 sm:gap-3 md:grid-cols-2 xl:grid-cols-4 md:gap-4">
      {STAT_CARDS.map((card, index) => {
        const Icon = card.icon;
        const value = stats[card.key] ?? 0;
        const active = activeKey === card.key;

        return (
          <motion.button
            key={card.key}
            type="button"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.04, duration: 0.28 }}
            onClick={() => onSelect?.(card.key)}
            title={`Lọc: ${card.label}`}
            className={`relative min-w-0 overflow-hidden rounded-xl md:rounded-2xl bg-gradient-to-br p-2 md:p-4 text-left text-white shadow-lg ring-1 ring-white/10 transition-all duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40 active:scale-[0.98] hover:-translate-y-0.5 ${
              active ? card.tone.shellActive : card.tone.shell
            }`}
          >
            {/* Icon chìm / họa tiết nền */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 overflow-hidden"
            >
              <Icon
                className={`absolute -right-2 -bottom-2 h-14 w-14 md:h-24 md:w-24 rotate-[-12deg] ${card.tone.motif}`}
                strokeWidth={1.25}
              />
              <div className="absolute -top-8 -left-8 h-20 w-20 rounded-full bg-white/5 blur-2xl" />
            </div>

            {/* Mobile */}
            <div className="relative z-[1] flex flex-col items-center justify-center gap-0.5 md:hidden">
              <div
                className={`rounded-lg p-1.5 ${card.tone.accent} ${
                  active ? "ring-1 ring-white/30" : ""
                }`}
              >
                <Icon className="h-3.5 w-3.5" strokeWidth={2.25} />
              </div>
              <p className="text-base font-black tabular-nums leading-none tracking-tight">
                {value}
              </p>
              <p className={`truncate text-[9px] font-bold uppercase tracking-wide ${card.tone.soft}`}>
                {card.shortLabel}
              </p>
            </div>

            {/* Desktop */}
            <div className="relative z-[1] hidden md:block">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-white/95">
                    {card.label}
                  </p>
                  <p className={`mt-1 text-[10px] font-medium ${card.tone.soft}`}>
                    {card.hint}
                  </p>
                </div>
                <div
                  className={`rounded-xl p-2 backdrop-blur-[2px] transition-transform ${card.tone.accent} ${
                    active ? "scale-110 ring-1 ring-white/35" : ""
                  }`}
                >
                  <Icon className="h-4 w-4" strokeWidth={2.25} />
                </div>
              </div>
              <p className="mt-3 text-3xl font-black tabular-nums tracking-tight drop-shadow-sm">
                {value}
              </p>
              {active ? (
                <p className="mt-2 text-[10px] font-bold uppercase tracking-widest text-white/70">
                  Đang lọc
                </p>
              ) : null}
            </div>
          </motion.button>
        );
      })}
    </div>
  </div>
);

export default SigningMiniDashboard;
